import { useEffect, useState } from "react";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

/**
 * SupportersPreviewRow — small avatar stack of the top N supporters with
 * a "View Top Supporters" CTA. Used on Track pages and Artist profiles.
 *
 * Props:
 *   kind        : "track" | "artist"
 *   targetId    : submission_id | user_id
 *   onViewAll   : () => void  // open the full modal
 *   refreshKey  : bump to refetch (real-time after gift sent)
 *   max         : avatars to show (default 5)
 */
const SupportersPreviewRow = ({
  kind, targetId, onViewAll, refreshKey = 0, max = 5,
}) => {
  const [supporters, setSupporters] = useState([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    const url = kind === "track"
      ? `/top-gifters/track/${targetId}?limit=${max}`
      : `/top-gifters/artist/${targetId}?limit=${max}`;
    api.get(url)
      .then((r) => {
        if (cancelled) return;
        setSupporters(r.data?.supporters || []);
        // Track endpoint doesn't return supporter_count (it's the supporters list only)
        // Artist endpoint does. Fall back to supporters length.
        setCount(
          typeof r.data?.supporter_count === "number"
            ? r.data.supporter_count
            : (r.data?.supporters || []).length
        );
      })
      .catch(() => { if (!cancelled) setSupporters([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [kind, targetId, refreshKey, max]);

  if (!loaded) return null;

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-pink-500/10 border border-amber-500/30"
      data-testid={`supporters-preview-${kind}`}
    >
      {supporters.length > 0 ? (
        <>
          <div className="flex -space-x-2">
            {supporters.map((s) => (
              <img
                key={s.user_id}
                src={getProfilePhotoUrl(s)}
                alt={s.artist_name}
                title={`${s.artist_name} · ${s.total_coins.toLocaleString()} coins`}
                className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a14] hover:scale-110 transition-transform"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                data-testid={`supporter-avatar-${s.user_id}`}
              />
            ))}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-amber-200 text-xs font-semibold">
              🏆 {count} Top Supporter{count === 1 ? "" : "s"}
            </p>
            <p className="text-white/70 text-[11px] truncate">
              Led by{" "}
              <span className="text-white font-semibold">
                {supporters[0].artist_name}
              </span>
              {supporters[0].top_gift?.emoji && (
                <span> · top {supporters[0].top_gift.emoji} {supporters[0].top_gift.name}</span>
              )}
            </p>
          </div>
        </>
      ) : (
        <div className="flex-1 min-w-0">
          <p className="text-amber-200 text-xs font-semibold">
            🏆 Top Supporters
          </p>
          <p className="text-pink-200/60 text-[11px]">
            No gifts yet — first supporter will be honored here.
          </p>
        </div>
      )}
      <button
        type="button"
        onClick={onViewAll}
        className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-400/40 font-semibold whitespace-nowrap"
        data-testid={`view-top-supporters-${kind}`}
      >
        View Top Supporters
      </button>
    </div>
  );
};

export default SupportersPreviewRow;
